import {
  createFaqCollection,
  faqExpectations,
  listFaqCollections,
  readFaqCollection,
  readFaqPropertyNames,
  sendFaqCommand,
  type FaqCollection,
  type FaqCommand,
} from '@/api/core/native-faq-shared';
import { OptionSelect } from '@/pages/settings/option-select';
import { Button, Checkbox, Input } from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { EditActionBar } from './edit-action-bar';
import { FaqSharedEditor } from './native-faq-shared-editor';
import { allowed } from './presentation/command-owner';
import { useCurrentCommand } from './presentation/use-current-command';
import type { PropertyRuntime } from './property-queries';

type Language = 'ko' | 'en' | 'und';
const LANGUAGE: Record<Language, string> = { ko: '한국어', en: 'English', und: '언어 미지정' };
const FLAGS = [
  ['detail', '지점 상세'],
  ['apply_completed', '신청 완료'],
  ['vacant_room', '빈방 알림'],
] as const;
const label = (item: FaqCollection['items'][number]) =>
  item.payload.question.ko ?? item.payload.question.en ?? item.title ?? '(질문 없음)';

/** 공유 FAQ 모음의 생성·항목·순서·지점 배정을 한곳에서 관리한다. 모든 쓰기는 현재 영향 지점 전체를 확인한 뒤 보낸다. */
export function NativeFaqManager({
  runtime,
  propertyId,
  onChanged,
}: {
  runtime: PropertyRuntime;
  propertyId: string;
  /** 모음이 바뀌어 이 지점의 FAQ 목록을 다시 읽어야 할 때 부른다. */
  onChanged: () => void;
}) {
  if (!allowed(runtime, 4)) return null;
  return <Manager runtime={runtime} propertyId={propertyId} onChanged={onChanged} />;
}

function Manager({
  runtime,
  propertyId,
  onChanged,
}: {
  runtime: PropertyRuntime;
  propertyId: string;
  onChanged: () => void;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [language, setLanguage] = useState<Language>('ko');
  const [order, setOrder] = useState('0');
  const created = useRef<string | null>(null);
  const cmd = useCurrentCommand<
    Awaited<ReturnType<typeof listFaqCollections>>,
    Parameters<typeof createFaqCollection>[1]
  >({
    runtime,
    scope: 'faq-collections',
    bits: 4,
    read: () => listFaqCollections(runtime.client),
    write: async (body) => {
      created.current = await createFaqCollection(runtime.client, body);
    },
    validate: () => Promise.resolve(),
  });
  useEffect(() => {
    if (cmd.success && created.current) {
      setSelected(created.current);
      created.current = null;
      setName('');
    }
  }, [cmd.success]);
  const pending = cmd.attempt && !cmd.attempt.accepted;
  const orderValid = /^\d{1,9}$/.test(order);
  return (
    <section aria-label="공유 FAQ 관리" className="space-y-3 rounded-md border p-4">
      <h3 className="font-semibold">공유 FAQ 모음 관리</h3>
      <p className="body-sm text-muted-foreground">
        공유 모음의 항목과 지점 배정은 바로 모든 배정 지점의 FAQ에 반영됩니다. 따로 게시하는 단계는
        없고, 한 지점만 따로 복사해 고치는 기능도 없습니다.
      </p>
      {cmd.error && (
        <p role="alert" className="body-sm">
          {cmd.error}
        </p>
      )}
      {!cmd.current && !cmd.error && <p role="status">공유 모음 목록을 확인하고 있습니다.</p>}
      {!cmd.current && cmd.error && (
        <Button
          variant="outline"
          onClick={() => {
            void cmd.refresh();
          }}
        >
          모음 목록 다시 조회
        </Button>
      )}
      {cmd.current && (
        <>
          <ul aria-label="공유 FAQ 모음 목록" className="body-sm space-y-1">
            {cmd.current.length === 0 && <li>아직 만든 공유 모음이 없습니다.</li>}
            {cmd.current.map((collection) => (
              <li key={collection.id}>
                <Button
                  variant={selected === collection.id ? 'solid' : 'outline'}
                  onClick={() => {
                    setSelected(collection.id);
                  }}
                >
                  {collection.name ?? '(이름 없는 모음)'} · {LANGUAGE[collection.language]}
                </Button>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-end gap-3" aria-label="새 공유 모음">
            <label className="grid gap-1">
              <span className="body-sm">모음 이름</span>
              <Input
                value={name}
                disabled={cmd.busy || !!pending}
                onChange={(e) => {
                  setName(e.target.value);
                }}
              />
            </label>
            <label className="grid gap-1">
              <span className="body-sm">언어</span>
              <OptionSelect
                aria-label="언어"
                className="w-40"
                value={language}
                options={Object.entries(LANGUAGE).map(([value, text]) => ({ value, label: text }))}
                onValueChange={(value) => {
                  setLanguage(value as Language);
                }}
              />
            </label>
            <label className="grid gap-1">
              <span className="body-sm">표시 순서</span>
              <Input
                wrapperClassName="w-24"
                inputMode="numeric"
                value={order}
                disabled={cmd.busy || !!pending}
                onChange={(e) => {
                  setOrder(e.target.value);
                }}
              />
            </label>
            <Button
              disabled={cmd.busy || (!pending && (!name.trim() || !orderValid))}
              onClick={() => {
                void cmd.submit({
                  command_id: crypto.randomUUID(),
                  owner_scope_kind: 'SHARED_FAQ',
                  purpose: 'FAQ',
                  language,
                  name: name.trim(),
                  sort_order: Number(order),
                });
              }}
            >
              {pending ? '같은 요청 다시 보내기' : '새 공유 모음 만들기'}
            </Button>
          </div>
          {selected && cmd.current.some((c) => c.id === selected) && (
            <CollectionPanel
              key={selected}
              runtime={runtime}
              propertyId={propertyId}
              collectionId={selected}
              onChanged={onChanged}
            />
          )}
        </>
      )}
    </section>
  );
}

interface Review {
  summary: string;
  build: () => FaqCommand;
  verify: (c: FaqCollection) => boolean;
}

function CollectionPanel({
  runtime,
  propertyId,
  collectionId,
  onChanged,
}: {
  runtime: PropertyRuntime;
  propertyId: string;
  collectionId: string;
  onChanged: () => void;
}) {
  // 최초 배정·재활성화일 때만 대상 지점의 콘텐츠 차수를 함께 읽는다. 다른 명령에는 영향 지점 전체만 쓴다.
  const [assigning, setAssigning] = useState(false);
  const cmd = useCurrentCommand<FaqCollection, FaqCommand>({
    runtime,
    scope: `faq-collection:${collectionId}`,
    readKey: String(assigning),
    bits: 4,
    read: () =>
      readFaqCollection(
        runtime.client,
        collectionId,
        undefined,
        assigning ? propertyId : undefined,
      ),
    write: (command) => sendFaqCommand(runtime.client, command),
    validate: () => Promise.resolve(),
    onActorChanged: () => {
      setReview(null);
      setSelecting(null);
      setOrderDraft(null);
    },
  });
  const names = useQuery({
    queryKey: ['faq-property-names', runtime.apiNamespace, collectionId],
    queryFn: ({ signal }) => readFaqPropertyNames(runtime.client, signal),
    retry: false,
    staleTime: 60_000,
    meta: { suppressGlobalError: true },
  });
  const [review, setReview] = useState<Review | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [verified, setVerified] = useState<boolean | null>(null);
  const [orderDraft, setOrderDraft] = useState<string[] | null>(null);
  const [newItem, setNewItem] = useState({
    qKo: '',
    qEn: '',
    aKo: '',
    aEn: '',
    detail: true,
    apply_completed: false,
    vacant_room: false,
  });
  const [selecting, setSelecting] = useState<{
    id: string;
    display: 'VISIBLE' | 'HIDDEN';
    mode: 'ALL' | 'EXPLICIT';
    ids: string[];
  } | null>(null);
  const sent = useRef<Review | null>(null);
  const handled = useRef(false);
  const current = cmd.current;
  const pending = cmd.attempt && !cmd.attempt.accepted;
  const items = (current?.items ?? [])
    .filter((item) => item.deleted_at === null)
    .sort((a, b) => a.sort_order - b.sort_order);
  const active = current?.assignments.filter((a) => a.deleted_at === null) ?? [];
  const mine = active.find((a) => a.property_id.toLowerCase() === propertyId.toLowerCase());
  const tombstone = current?.assignments.find(
    (a) => a.deleted_at !== null && a.property_id.toLowerCase() === propertyId.toLowerCase(),
  );
  const propertyName = (id: string) =>
    id.toLowerCase() === propertyId.toLowerCase()
      ? '이 지점'
      : (names.data?.get(id.toLowerCase()) ?? '이름을 확인하지 못한 지점');
  const affected = active.map((a) => propertyName(a.property_id));

  useEffect(() => {
    if (cmd.success && !handled.current) {
      handled.current = true;
      setVerified(current && sent.current ? sent.current.verify(current) : true);
      setReview(null);
      setAcknowledged(false);
      setOrderDraft(null);
      setAssigning(false);
      setSelecting(null);
      onChanged();
    }
    if (!cmd.success) handled.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmd.success, current]);

  const start = (next: Review) => {
    setReview(next);
    setAcknowledged(false);
    setVerified(null);
  };
  const base = () => {
    if (!current) throw new Error('현재 모음을 확인하지 못했습니다.');
    return { command_id: crypto.randomUUID(), ...faqExpectations(current) };
  };
  const apply = () => {
    if (!review) return;
    sent.current = review;
    void cmd.submit(review.build());
  };
  const ordered = orderDraft ?? items.map((item) => item.id);
  const move = (index: number, by: -1 | 1) => {
    const next = [...ordered];
    const target = index + by;
    if (target < 0 || target >= next.length) return;
    const [moved] = next.splice(index, 1);
    if (moved === undefined) return;
    next.splice(target, 0, moved);
    setOrderDraft(next);
  };
  const itemById = new Map(items.map((item) => [item.id, item]));

  return (
    <section aria-label="공유 모음 상세" className="space-y-4 border-t pt-3">
      {cmd.error && (
        <p role="alert" className="body-sm">
          {cmd.error}
        </p>
      )}
      {!current && !cmd.error && <p role="status">공유 모음의 현재 상태를 확인하고 있습니다.</p>}
      {!current && cmd.error && (
        <Button
          variant="outline"
          onClick={() => {
            void cmd.refresh();
          }}
        >
          모음 다시 조회
        </Button>
      )}
      {current && (
        <>
          <h4 className="font-medium">
            {current.name ?? '(이름 없는 모음)'} · {LANGUAGE[current.language]}
          </h4>
          <div>
            <p className="body-sm font-medium">지금 이 모음을 쓰는 지점 ({affected.length})</p>
            <ul aria-label="이 모음을 쓰는 지점" className="body-sm list-disc pl-5">
              {affected.length === 0 && <li>배정된 지점이 없습니다.</li>}
              {active.map((a) => (
                <li key={a.id}>
                  {propertyName(a.property_id)} · {a.display_state === 'HIDDEN' ? '숨김' : '보임'} ·{' '}
                  {a.selection_mode === 'ALL'
                    ? '항목 전체'
                    : `선택한 ${String(a.item_ids.length)}개 항목`}
                  <Button
                    variant="outline"
                    disabled={cmd.busy || !!pending || !!review}
                    onClick={() => {
                      setSelecting({
                        id: a.id,
                        display: a.display_state,
                        mode: a.selection_mode,
                        ids: a.item_ids,
                      });
                    }}
                  >
                    표시·항목 선택 변경
                  </Button>
                  <Button
                    variant="outline"
                    disabled={cmd.busy || !!pending || !!review}
                    onClick={() => {
                      start({
                        summary: `${propertyName(a.property_id)}에서 이 모음 배정 해제`,
                        build: () => ({ op: 'retire', assignmentId: a.id, body: base() }),
                        verify: (c) =>
                          c.assignments.some((x) => x.id === a.id && x.deleted_at !== null),
                      });
                    }}
                  >
                    배정 해제
                  </Button>
                </li>
              ))}
            </ul>
            {selecting && !review && (
              <div className="mt-2 space-y-2" aria-label="표시·항목 선택">
                <p className="body-sm font-medium">
                  {propertyName(active.find((x) => x.id === selecting.id)?.property_id ?? '')}의
                  표시
                </p>
                <label className="flex items-center gap-1">
                  <Checkbox
                    checked={selecting.display === 'HIDDEN'}
                    onCheckedChange={(checked) => {
                      setSelecting({
                        ...selecting,
                        display: checked ? 'HIDDEN' : 'VISIBLE',
                      });
                    }}
                  />
                  <span className="body-sm">이 지점 FAQ에서 이 모음 숨기기</span>
                </label>
                <label className="flex items-center gap-1">
                  <Checkbox
                    checked={selecting.mode === 'EXPLICIT'}
                    onCheckedChange={(checked) => {
                      setSelecting({ ...selecting, mode: checked ? 'EXPLICIT' : 'ALL' });
                    }}
                  />
                  <span className="body-sm">고른 항목만 보이기 (끄면 항목 전체)</span>
                </label>
                {selecting.mode === 'EXPLICIT' && (
                  <ul aria-label="보일 항목" className="body-sm">
                    {items.map((item) => (
                      <li key={item.id}>
                        <label className="flex items-center gap-1">
                          <Checkbox
                            checked={selecting.ids.includes(item.id)}
                            onCheckedChange={(checked) => {
                              setSelecting({
                                ...selecting,
                                ids: checked
                                  ? [...selecting.ids, item.id]
                                  : selecting.ids.filter((x) => x !== item.id),
                              });
                            }}
                          />
                          <span>{label(item)}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
                <Button
                  disabled={cmd.busy || !!pending}
                  onClick={() => {
                    const draft = selecting;
                    const ids =
                      draft.mode === 'ALL'
                        ? []
                        : items.filter((i) => draft.ids.includes(i.id)).map((i) => i.id);
                    start({
                      summary: `${propertyName(active.find((x) => x.id === draft.id)?.property_id ?? '')}의 표시·항목 선택 변경`,
                      build: () => ({
                        op: 'selection',
                        assignmentId: draft.id,
                        body: {
                          ...base(),
                          display_state: draft.display,
                          selection_mode: draft.mode,
                          item_ids: ids,
                        },
                      }),
                      verify: (c) =>
                        c.assignments.some(
                          (x) =>
                            x.id === draft.id &&
                            x.display_state === draft.display &&
                            x.selection_mode === draft.mode &&
                            JSON.stringify(x.item_ids) === JSON.stringify(ids),
                        ),
                    });
                  }}
                >
                  선택 내용 확인
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelecting(null);
                  }}
                >
                  선택 변경 취소
                </Button>
              </div>
            )}
            {!mine && (
              <Button
                variant="outline"
                disabled={cmd.busy || !!pending || !!review}
                onClick={() => {
                  setAssigning(true);
                }}
              >
                {tombstone ? '이 지점에 다시 배정' : '이 지점에 배정'}
              </Button>
            )}
            {assigning && !mine && (
              <div className="mt-2 space-y-2" aria-label="이 지점 배정">
                <p className="body-sm text-muted-foreground">
                  배정하면 모음의 모든 항목이 이 지점 FAQ에 보입니다. 항목을 고르려면 배정 뒤 선택을
                  바꾸세요.
                </p>
                <Button
                  disabled={cmd.busy || !!pending || !!review}
                  onClick={() => {
                    start({
                      summary: '이 지점에 모음 배정',
                      build: () => ({
                        op: 'assign',
                        collectionId,
                        body: {
                          ...base(),
                          scope_kind: 'PROPERTY',
                          property_id: propertyId,
                          purpose: 'FAQ',
                          sort_order: 0,
                          reactivate: !!tombstone,
                          display_state: 'VISIBLE',
                          selection_mode: 'ALL',
                          item_ids: [],
                        },
                      }),
                      verify: (c) =>
                        c.assignments.some(
                          (x) =>
                            x.deleted_at === null &&
                            x.property_id.toLowerCase() === propertyId.toLowerCase() &&
                            x.display_state === 'VISIBLE' &&
                            x.selection_mode === 'ALL',
                        ),
                    });
                  }}
                >
                  배정 내용 확인
                </Button>
                <Button
                  variant="outline"
                  disabled={cmd.busy || !!pending}
                  onClick={() => {
                    setAssigning(false);
                    setReview(null);
                  }}
                >
                  배정 취소
                </Button>
              </div>
            )}
          </div>

          <div>
            <p className="body-sm font-medium">항목 ({items.length})</p>
            <ol aria-label="모음 항목" className="body-sm space-y-2">
              {ordered.map((id, index) => {
                const item = itemById.get(id);
                if (!item) return null;
                return (
                  <li key={id} className="space-y-1">
                    <span>{label(item)}</span>{' '}
                    <Button
                      variant="outline"
                      aria-label={`${label(item)} 위로`}
                      disabled={index === 0 || cmd.busy || !!pending || !!review}
                      onClick={() => {
                        move(index, -1);
                      }}
                    >
                      위로
                    </Button>
                    <Button
                      variant="outline"
                      aria-label={`${label(item)} 아래로`}
                      disabled={index === ordered.length - 1 || cmd.busy || !!pending || !!review}
                      onClick={() => {
                        move(index, 1);
                      }}
                    >
                      아래로
                    </Button>
                    <FaqSharedEditor
                      runtime={runtime}
                      propertyId={propertyId}
                      collectionId={collectionId}
                      item={item}
                      onSaved={() => {
                        void cmd.refresh();
                        onChanged();
                      }}
                    />
                  </li>
                );
              })}
            </ol>
            {orderDraft && !review && (
              <Button
                disabled={cmd.busy || !!pending}
                onClick={() => {
                  const ids = ordered;
                  start({
                    summary: '항목 순서 변경',
                    build: () => ({
                      op: 'item-order',
                      collectionId,
                      body: { ...base(), item_ids: ids },
                    }),
                    verify: (c) =>
                      JSON.stringify(
                        c.items
                          .filter((i) => i.deleted_at === null)
                          .sort((x, y) => x.sort_order - y.sort_order)
                          .map((i) => i.id),
                      ) === JSON.stringify(ids),
                  });
                }}
              >
                순서 변경 내용 확인
              </Button>
            )}
          </div>

          <div className="space-y-2" aria-label="항목 추가">
            <p className="body-sm font-medium">항목 추가</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {(
                [
                  ['qKo', '질문 · 한국어'],
                  ['qEn', '질문 · English'],
                  ['aKo', '답변 · 한국어'],
                  ['aEn', '답변 · English'],
                ] as const
              ).map(([key, text]) => (
                <label key={key} className="grid gap-1">
                  <span className="body-sm">{`새 ${text}`}</span>
                  <Input
                    value={newItem[key]}
                    disabled={cmd.busy || !!pending || !!review}
                    onChange={(e) => {
                      setNewItem({ ...newItem, [key]: e.target.value });
                    }}
                  />
                </label>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              {FLAGS.map(([key, text]) => (
                <label key={key} className="flex items-center gap-1">
                  <Checkbox
                    checked={newItem[key]}
                    disabled={cmd.busy || !!pending || !!review}
                    onCheckedChange={(checked) => {
                      setNewItem({ ...newItem, [key]: checked });
                    }}
                  />
                  <span className="body-sm">{text} 페이지에 보임</span>
                </label>
              ))}
            </div>
            <Button
              variant="outline"
              disabled={
                cmd.busy ||
                !!pending ||
                !!review ||
                !(newItem.qKo.trim() || newItem.qEn.trim()) ||
                !(newItem.aKo.trim() || newItem.aEn.trim())
              }
              onClick={() => {
                const draft = { ...newItem };
                const orNull = (v: string) => (v.trim() === '' ? null : v);
                start({
                  summary: `항목 "${draft.qKo || draft.qEn}" 추가`,
                  build: () => ({
                    op: 'item-create',
                    collectionId,
                    body: {
                      ...base(),
                      kind: 'FAQ',
                      title: null,
                      payload: {
                        question: { ko: orNull(draft.qKo), en: orNull(draft.qEn) },
                        answer: { ko: orNull(draft.aKo), en: orNull(draft.aEn) },
                        detail: draft.detail,
                        apply_completed: draft.apply_completed,
                        vacant_room: draft.vacant_room,
                        membership_state: 'CURRENT',
                        source_order: null,
                      },
                    },
                  }),
                  verify: (c) =>
                    c.items.filter((i) => i.deleted_at === null).length === items.length + 1,
                });
              }}
            >
              항목 추가 내용 확인
            </Button>
          </div>

          {review && (
            <div className="space-y-2" aria-label="변경 영향 확인">
              <p className="body-sm" role="status">
                {review.summary}: 저장하면 위 {affected.length}개 지점에 바로 반영됩니다.
              </p>
              <label className="flex items-start gap-2">
                <Checkbox
                  checked={acknowledged}
                  disabled={cmd.busy}
                  onCheckedChange={(checked) => {
                    setAcknowledged(checked);
                  }}
                />
                <span className="body-sm">영향받는 지점 모두에 반영되는 것을 확인했습니다.</span>
              </label>
              <EditActionBar label="공유 FAQ 저장 동작">
                <Button disabled={!acknowledged || cmd.busy} onClick={apply}>
                  {pending ? '같은 요청 다시 보내기' : '영향 지점 모두에 반영해 저장'}
                </Button>
                <Button
                  variant="outline"
                  disabled={cmd.busy || !!pending}
                  onClick={() => {
                    setReview(null);
                  }}
                >
                  취소
                </Button>
              </EditActionBar>
            </div>
          )}
          {verified !== null && (
            <p role="status">
              {verified
                ? '저장 후 같은 모음을 다시 조회해 요청한 변경이 반영된 것을 확인했습니다.'
                : '저장 후 다시 조회한 모음이 요청과 다릅니다. 위 현재 상태를 확인해 주세요.'}
            </p>
          )}
        </>
      )}
    </section>
  );
}
